import React, { useMemo } from 'react';
import {
  Box,
  Button,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { Add, ArrowDownward, ArrowUpward, Delete } from '@mui/icons-material';
import type {
  FundSubtypeOption,
  FundTypeOption,
  TransferAllocationMode,
} from '../../types/financeFunds';
import {
  fundTypeRequiresCustomName,
  fundSubtypeRequiresNamedLimit,
  fundSubtypeIsSpecialRecognition,
  NAMED_SCHOLARSHIP_LIMIT_OPTIONS,
  TRANSFER_ALLOCATION_MODE_ORDER,
} from '../../types/financeFunds';

export interface AssignedTypeRow {
  type_id: string;
  subtype_id: string;
  percentage: string;
  other_transfer_name: string;
  amount: string;
  named_limit: string;
  special_recognition_count: string;
}

interface FundTypeAllocationEditorProps {
  rows: AssignedTypeRow[];
  categoryId: string;
  mode: TransferAllocationMode;
  typeOptions: FundTypeOption[];
  subtypeOptions: FundSubtypeOption[];
  onChange: (rows: AssignedTypeRow[]) => void;
}

const FundTypeAllocationEditor: React.FC<FundTypeAllocationEditorProps> = ({
  rows,
  categoryId,
  mode,
  typeOptions,
  subtypeOptions,
  onChange,
}) => {
  const isOrderMode = mode === TRANSFER_ALLOCATION_MODE_ORDER;

  const filteredTypes = useMemo(
    () => typeOptions.filter((t) => t.category_id === categoryId),
    [typeOptions, categoryId]
  );

  const percentRows = useMemo(
    () =>
      rows.filter((row) => {
        if (fundSubtypeIsSpecialRecognition(row.subtype_id)) return false;
        const n = parseFloat(row.percentage);
        return Number.isFinite(n) && n > 0;
      }),
    [rows]
  );

  const total = useMemo(
    () =>
      percentRows.reduce((sum, row) => {
        const n = parseFloat(row.percentage);
        return sum + (Number.isFinite(n) ? n : 0);
      }, 0),
    [percentRows]
  );

  const hasPercentRows = percentRows.length > 0;
  // Match server: round to 4 decimal places, then must equal 100.
  const totalRounded = Math.round((total + Number.EPSILON) * 10000) / 10000;
  const totalValid = !hasPercentRows || Math.abs(totalRounded - 100) < 0.00005;

  const subtypesForType = (typeId: string) =>
    subtypeOptions.filter((s) => s.type_id === typeId);

  const updateRow = (index: number, patch: Partial<AssignedTypeRow>) => {
    const next = rows.map((row, i) =>
      i === index ? { ...row, ...patch } : row
    );
    onChange(next);
  };

  const addRow = () => {
    onChange([
      ...rows,
      {
        type_id: '',
        subtype_id: '',
        percentage: '',
        other_transfer_name: '',
        amount: '',
        named_limit: '',
        special_recognition_count: '',
      },
    ]);
  };

  const removeRow = (index: number) => {
    onChange(rows.filter((_, i) => i !== index));
  };

  const moveRow = (index: number, delta: number) => {
    const nextIndex = index + delta;
    if (nextIndex < 0 || nextIndex >= rows.length) return;
    const next = [...rows];
    const [item] = next.splice(index, 1);
    next.splice(nextIndex, 0, item);
    onChange(next);
  };

  return (
    <Grid item xs={12}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Type allocations
      </Typography>
      {isOrderMode && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Rows apply top-to-bottom. Each percentage is of remaining income;
          percentages need not total 100% and leftover may remain unallocated.
        </Typography>
      )}
      {rows.map((row, index) => {
        const rowSubtypes = subtypesForType(row.type_id);
        const subtypeRequired = rowSubtypes.length > 0;
        return (
          <Box
            key={`alloc-row-${index}`}
            sx={{
              display: 'flex',
              gap: 1,
              mb: 1,
              alignItems: 'flex-start',
              flexWrap: 'wrap',
            }}
          >
            {isOrderMode && (
              <Box sx={{ display: 'flex', flexDirection: 'column', pt: 0.5 }}>
                <IconButton
                  aria-label="Move allocation row up"
                  onClick={() => moveRow(index, -1)}
                  disabled={index === 0}
                  size="small"
                >
                  <ArrowUpward fontSize="small" />
                </IconButton>
                <IconButton
                  aria-label="Move allocation row down"
                  onClick={() => moveRow(index, 1)}
                  disabled={index === rows.length - 1}
                  size="small"
                >
                  <ArrowDownward fontSize="small" />
                </IconButton>
              </Box>
            )}
            <FormControl size="small" sx={{ minWidth: 180, flex: 1 }}>
              <InputLabel>Type</InputLabel>
              <Select
                label="Type"
                value={row.type_id}
                onChange={(e) => {
                  const type_id = String(e.target.value);
                  updateRow(index, {
                    type_id,
                    subtype_id: '',
                    other_transfer_name: '',
                    named_limit: '',
                    special_recognition_count: '',
                  });
                }}
              >
                <MenuItem value="">
                  <em>Select type</em>
                </MenuItem>
                {filteredTypes.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {subtypeRequired && (
              <FormControl size="small" sx={{ minWidth: 160, flex: 1 }}>
                <InputLabel>Subtype</InputLabel>
                <Select
                  label="Subtype"
                  value={row.subtype_id}
                  onChange={(e) => {
                    const subtype_id = String(e.target.value);
                    const special = fundSubtypeIsSpecialRecognition(subtype_id);
                    updateRow(index, {
                      subtype_id,
                      named_limit:
                        subtype_id && fundSubtypeRequiresNamedLimit(subtype_id)
                          ? row.named_limit
                          : '',
                      amount: special ? '' : row.amount,
                      percentage: special ? '' : row.percentage,
                      special_recognition_count: special
                        ? row.special_recognition_count.trim() || '1'
                        : '',
                    });
                  }}
                >
                  <MenuItem value="">
                    <em>Select subtype</em>
                  </MenuItem>
                  {rowSubtypes.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {fundSubtypeRequiresNamedLimit(row.subtype_id) && (
              <FormControl size="small" sx={{ minWidth: 180, flex: 1 }}>
                <InputLabel>Named scholarship limit</InputLabel>
                <Select
                  label="Named scholarship limit"
                  value={row.named_limit}
                  onChange={(e) =>
                    updateRow(index, { named_limit: String(e.target.value) })
                  }
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {NAMED_SCHOLARSHIP_LIMIT_OPTIONS.map((n) => (
                    <MenuItem key={n} value={String(n)}>
                      {n}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {fundTypeRequiresCustomName(row.type_id) && (
              <TextField
                label="Custom name"
                size="small"
                required
                value={row.other_transfer_name}
                onChange={(e) =>
                  updateRow(index, { other_transfer_name: e.target.value })
                }
                sx={{ minWidth: 200, flex: 1 }}
                inputProps={{ maxLength: 255 }}
              />
            )}
            {fundSubtypeIsSpecialRecognition(row.subtype_id) ? (
              <TextField
                label="Recognitions"
                size="small"
                type="number"
                value={row.special_recognition_count}
                onChange={(e) =>
                  updateRow(index, {
                    special_recognition_count: e.target.value,
                  })
                }
                sx={{ minWidth: 140, flex: 1 }}
                inputProps={{ min: 1, max: 20, step: '1' }}
                helperText="How many $500 awards"
              />
            ) : (
              <TextField
                label="Amount"
                size="small"
                type="number"
                value={row.amount}
                onChange={(e) =>
                  updateRow(index, {
                    amount: e.target.value,
                    percentage: e.target.value.trim() ? '' : row.percentage,
                  })
                }
                sx={{ minWidth: 120, flex: 1 }}
                inputProps={{ min: 0.01, step: '0.01' }}
                helperText={row.amount.trim() ? 'Uses set amount' : undefined}
              />
            )}
            {!fundSubtypeIsSpecialRecognition(row.subtype_id) && (
              <TextField
                label="%"
                size="small"
                type="number"
                value={row.percentage}
                onChange={(e) =>
                  updateRow(index, {
                    percentage: e.target.value,
                    amount: e.target.value.trim() ? '' : row.amount,
                  })
                }
                sx={{ width: 90 }}
                inputProps={{ min: 0, max: 100, step: '0.0001' }}
              />
            )}
            <IconButton
              aria-label="Remove allocation row"
              onClick={() => removeRow(index)}
              disabled={rows.length <= 1}
              size="small"
            >
              <Delete />
            </IconButton>
          </Box>
        );
      })}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
        <Button startIcon={<Add />} size="small" onClick={addRow}>
          Add type
        </Button>
        {hasPercentRows && (
          <Typography
            variant="body2"
            color={isOrderMode || totalValid ? 'text.secondary' : 'error'}
          >
            Total: {totalRounded.toFixed(4)}%
            {isOrderMode && !totalValid
              ? ' (need not equal 100 in order mode)'
              : ''}
          </Typography>
        )}
      </Box>
    </Grid>
  );
};

export default FundTypeAllocationEditor;
